import { useState, useEffect } from 'react';
import apiService from '../../../services/api';
import { getParamLabel } from '../../../constants/parameterLabels';
import { SEO_AS_IS_TABS } from '../../../constants/telemetryModules';
import { usePageTracking } from '../../../hooks/usePageTracking';

interface ActionPlanTrackingProps {
  onTooltipShow: (title: string, content: string) => void;
}

interface ParameterGroup {
  parameter_group: string;
  total: number;
  completed: number;
  in_progress: number;
  not_started: number;
  completion_percentage: number;
}

interface RecentTask {
  task_id: number;
  task_title: string;
  old_status: string;
  new_status: string;
  priority: string;
  changed_at: string;
  parameter_group: string;
}

interface OverviewData {
  overview: {
    total_tasks: number;
    completed: number;
    in_progress: number;
    pending: number;
    completion_rate: number;
  };
  parameter_groups: ParameterGroup[];
  recent_activity: RecentTask[];
}

export default function ActionPlanTracking({ onTooltipShow }: ActionPlanTrackingProps) {
  const [data, setData] = useState<OverviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'onpage' | 'offpage' | 'technical'>('onpage');
  usePageTracking({
    moduleName: 'SEO Manager',
    submoduleName: `Action Plan Tracking / ${SEO_AS_IS_TABS[activeTab]}`,
    routePath: `/dashboard#action-plan-tracking/${activeTab}`,
  });

  // Fetch data when tab changes
  useEffect(() => {
    fetchCategoryData(activeTab);
  }, [activeTab]);

  const fetchCategoryData = async (category: 'onpage' | 'offpage' | 'technical') => {
    try {
      setLoading(true);
      const userId = localStorage.getItem('user_id');
      if (!userId) {
        console.error('No user_id found');
        setLoading(false);
        return;
      }

      // Use category-specific endpoint
      const resp = await apiService.getActionPlanCategoryOverview(userId, category) as any;
      console.log(`ActionPlanTracking ${category} response:`, resp);

      if (resp && resp.data) {
        setData(resp.data);
      }
    } catch (error) {
      console.error('Error fetching action plan data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'text-green-600 bg-green-100 border-green-200';
      case 'in_progress':
      case 'in-progress':
        return 'text-blue-600 bg-blue-100 border-blue-200';
      case 'pending':
      case 'not_started':
        return 'text-gray-600 bg-gray-100 border-gray-200';
      default:
        return 'text-gray-600 bg-gray-100 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return 'ri-check-line';
      case 'in_progress':
      case 'in-progress':
        return 'ri-time-line';
      default:
        return 'ri-hourglass-line';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority?.toLowerCase()) {
      case 'high':
        return 'text-red-600 bg-red-100';
      case 'medium':
        return 'text-yellow-600 bg-yellow-100';
      case 'low':
        return 'text-green-600 bg-green-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '—';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
            <div className="h-48 bg-gray-200 dark:bg-gray-700 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="space-y-6">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Action Plan Tracking</h2>
          <p className="text-gray-600 dark:text-gray-400">No action plan data available. Generate tasks from Strategy {'>'} Action Plan first.</p>
        </div>
      </div>
    );
  }

  const { overview, parameter_groups, recent_activity } = data;
  const overallProgress = Math.round(overview.completion_rate || 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Action Plan Tracking</h2>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => fetchCategoryData(activeTab)}
              className="px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg transition-colors duration-200 flex items-center space-x-2"
              disabled={loading}
            >
              <i className={`ri-refresh-line ${loading ? 'animate-spin' : ''}`}></i>
              <span>Refresh</span>
            </button>
            <button
              onClick={() => onTooltipShow('Action Plan Tracking', 'Monitor the progress of your action plan tasks. Track completed tasks, work in progress, and pending items. Data is fetched in real-time from your task database.')}
              className="text-gray-400 hover:text-purple-500"
            >
              <i className="ri-information-line text-xl"></i>
            </button>
          </div>
        </div>
        <p className="text-gray-600 dark:text-gray-400">Track your action plan execution and task completion</p>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-lg border border-gray-200 dark:border-gray-700">
        <div className="flex space-x-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
          {[
            { id: 'onpage', label: 'On-Page', icon: 'ri-file-text-line' },
            { id: 'offpage', label: 'Off-Page', icon: 'ri-links-line' },
            { id: 'technical', label: 'Core Web Vitals', icon: 'ri-speed-line' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as 'onpage' | 'offpage' | 'technical')}
              className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-lg transition-all duration-200 ${activeTab === tab.id
                ? 'bg-white dark:bg-gray-600 text-purple-600 dark:text-purple-400 shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
            >
              <i className={`${tab.icon} text-lg`}></i>
              <span className="font-medium whitespace-nowrap">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Overall Progress Card */}

      <div className="bg-gradient-to-r from-purple-500 to-violet-600 rounded-xl p-6 shadow-lg">
        <div className="flex items-center justify-between text-white mb-4">
          <div>
            <h3 className="text-xl font-bold mb-1">{activeTab === 'onpage' ? 'On-Page' : activeTab === 'offpage' ? 'Off-Page' : 'Core Web Vitals'} Progress</h3>
            <p className="text-purple-100">{overview.total_tasks} total tasks</p>
          </div>
          <div className="text-right">
            <div className="text-4xl font-bold">{overallProgress}%</div>
            <div className="text-sm text-purple-100">Complete</div>
          </div>
        </div>

        <div className="h-3 bg-white/30 rounded-full overflow-hidden mb-4">
          <div
            className="h-full bg-white rounded-full transition-all duration-500"
            style={{ width: `${overallProgress}%` }}
          ></div>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div className="bg-white/20 rounded-lg p-3">
            <div className="text-2xl font-bold text-white">{overview.completed}</div>
            <div className="text-xs text-purple-100">Completed</div>
          </div>
          <div className="bg-white/20 rounded-lg p-3">
            <div className="text-2xl font-bold text-white">{overview.in_progress}</div>
            <div className="text-xs text-purple-100">In Progress</div>
          </div>
          <div className="bg-white/20 rounded-lg p-3">
            <div className="text-2xl font-bold text-white">{overview.pending}</div>
            <div className="text-xs text-purple-100">Pending</div>
          </div>
        </div>
      </div>

      {/* Category Progress */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Progress by Parameter Group</h3>
        <div className="space-y-4">
          {parameter_groups.length > 0 ? (
            parameter_groups.map((group, index) => (
              <div key={index} className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white">{getParamLabel(group.parameter_group)}</h4>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      {group.completed} of {group.total} tasks completed
                    </p>
                  </div>
                  <span className="text-lg font-bold text-purple-600">{group.completion_percentage.toFixed(0)}%</span>
                </div>
                <div className="h-2 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-purple-500 to-violet-600 rounded-full transition-all duration-500"
                    style={{ width: `${group.completion_percentage}%` }}
                  ></div>
                </div>
              </div>
            ))
          ) : (
            <p className="text-gray-500 dark:text-gray-400 text-center py-8">
              No parameter groups found. Generate tasks to see progress breakdown.
            </p>
          )}
        </div>
      </div>

      {/* Recent Task Activity */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Recent Task Activity</h3>
        <div className="space-y-3">
          {recent_activity.length > 0 ? (
            recent_activity.map((task, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg hover:shadow-md transition-shadow"
              >
                <div className="flex items-center space-x-4 flex-1">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center border ${getStatusColor(task.new_status)}`}>
                    <i className={`${getStatusIcon(task.new_status)} text-lg`}></i>
                  </div>
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">{task.task_title}</h4>
                    <div className="flex items-center space-x-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(task.new_status)}`}>
                        {task.new_status?.replace('_', ' ').toUpperCase()}
                      </span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(task.priority)}`}>
                        {task.priority?.toUpperCase()}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {formatDate(task.changed_at)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <p className="text-gray-500 dark:text-gray-400 text-center py-8">
              No recent activity. Complete or update tasks to see activity here.
            </p>
          )}
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3 mb-3">
            <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
              <i className="ri-check-line text-2xl text-green-600"></i>
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{overview.completed}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Tasks Completed</div>
            </div>
          </div>
          <div className="text-xs text-green-600 dark:text-green-400">
            {overview.total_tasks > 0 ? Math.round((overview.completed / overview.total_tasks) * 100) : 0}% of total tasks
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3 mb-3">
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
              <i className="ri-time-line text-2xl text-blue-600"></i>
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{overview.in_progress}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">In Progress</div>
            </div>
          </div>
          <div className="text-xs text-blue-600 dark:text-blue-400">
            Currently being worked on
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3 mb-3">
            <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
              <i className="ri-hourglass-line text-2xl text-gray-600"></i>
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{overview.pending}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Pending Tasks</div>
            </div>
          </div>
          <div className="text-xs text-gray-600 dark:text-gray-400">
            Waiting to be started
          </div>
        </div>
      </div>
    </div>
  );
}
